import { Button, message } from "antd";
import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getUserProfile,
  updateUserProfile,
} from "../../../../controller/services/about";
import i18n from "../../../../localization/i18n";
import {
  Duration,
  FinalSteps,
  HouseholdPage,
  ImmigrationStatusPage,
  LiveInCanadaPage,
  LocationForm,
  NewLocationForm,
} from "../../../../Pages/Onboarding";
import NameAgePage from "../../../../Pages/Onboarding/NameAge";
import {
  setAnsweredQuestions,
  setInnerStepper,
  setLoading,
  setTotalQuestions,
} from "../../../../redux/slices/common_slice";
import { routePaths } from "../../../../routing/routePaths";
import CreateProfileCenterWrapper from "../../CreateProfileCenterWrapper";
import CreateProfileFooter from "../../CreateProfileFooter";
import CreateProfileHeader from "../../CreateProfileHeader";

const AboutYouChild = () => {
  const innerStepper = useSelector((state) => state.common?.innerStepperKey);
  const dispatch = useDispatch();
  const formik = useFormik({
    initialValues: {
      firstName: "",
      ageRange: "",
      livingInCanada: "",
      yearsLivingInCanada: undefined,
      province: undefined,
      city: undefined,
      provinceNew: undefined,
      cityNew: undefined,
      selectedStatus: "",
      houseHold: [{ type: "myself" }],
      currentStep: 0,
      answered: 0,
    },
    validateOnBlur: true,
    onSubmit: (values) => onFinish(values),
  });

  const stepper = [
    {
      step: 0,
      Component: NameAgePage,
    },
    {
      step: 1,
      Component: LiveInCanadaPage,
    },
    {
      step: 2,
      Component: Duration,
    },
    {
      step: 3,
      Component: LocationForm,
    },
    {
      step: 4,
      Component: NewLocationForm,
    },
    {
      step: 5,
      Component: ImmigrationStatusPage,
    },
    {
      step: 6,
      Component: HouseholdPage,
      IS_EDITABLE: true,
    },
    {
      step: 7,
      Component: null,
      IS_REVIEW: true,
    },
    {
      step: 8,
      Component: FinalSteps,
      IS_FINAL: true,
    },
  ];
  const foundItem = stepper.find((i) => i.step == innerStepper);
  const [editableStep, setEditableStep] = useState(null);
  const [savingsObj, setSavingsObj] = useState({ type: "", amount: "" });
  const stepperStatus = useSelector((state) => state.common?.stepperStatus);
  const navigate = useNavigate();
  const [respStep, setRespStep] = useState(0);
  const containerRef = useRef();
  const { t } = useTranslation();
  const handleNextStep = () => {
    if (!foundItem?.IS_FINAL) {
      if (formik.values.livingInCanada === "yes" && innerStepper === 3) {
        dispatch(setInnerStepper(5));
        formik.setFieldValue("currentStep", 5);
      } else if (formik.values.livingInCanada === "no" && innerStepper === 1) {
        navigate(routePaths.notLivingInCanada);
      } else if (
        formik.values.livingInCanada === "justCurious" &&
        innerStepper === 1
      ) {
        navigate(routePaths.notLivingInCanada);
      } else {
        dispatch(setInnerStepper(innerStepper + 1));
        formik.setFieldValue("currentStep", formik.values.currentStep + 1);
      }
    }
    dispatch(setLoading(false));
  };

  useEffect(() => {
    if (stepperStatus?.isProfileCompleted) {
      dispatch(setInnerStepper(8));
    } else if (respStep) {
      dispatch(setInnerStepper(respStep));
    } else {
      dispatch(setInnerStepper(0));
    }
  }, [stepperStatus?.isProfileCompleted, respStep]);

  const onFinish = (values) => {
    dispatch(setLoading(true));
    const payload = { ...values };
    if (foundItem?.IS_FINAL) {
      payload["isProfileCompletion"] = true;
    } else {
      payload["step"] = values.currentStep + 1;
      payload["immigrationStatus"] = values.selectedStatus;
      payload["province"] = values?.province || values?.provinceNew;
      payload["city"] = values?.city || values?.cityNew;
    }
    delete payload?.currentStep;
    delete payload.selectedStatus;
    updateUserProfile(payload)
      .then((resp) => {
        if (!foundItem?.IS_FINAL) {
          handleNextStep();
        }
        if (innerStepper === 7) {
          ReactGA.send({
            hitType: "event",
            eventCategory: "profile",
            eventAction: "about_you_completion",
            eventLabel: "Profile Status",
            // Custom parameters
            profile_completion: true,
          });
        }
      })
      .catch((Err) => {
        message.error(Err.message[i18n.language]);
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  const handlePreviousStep = () => {
    if (formik.values.livingInCanada === "yes" && innerStepper === 5) {
      dispatch(setInnerStepper(3));
      formik.setFieldValue("currentStep", 3);
    } else if (formik.values.livingInCanada === "yes" && innerStepper === 3) {
      dispatch(setInnerStepper(2));
      formik.setFieldValue("currentStep", 2);
    } else if (formik.values.livingInCanada === "no" && innerStepper === 5) {
      dispatch(setInnerStepper(4));
      formik.setFieldValue("currentStep", 4);
    } else if (formik.values.livingInCanada === "no" && innerStepper === 4) {
      dispatch(setInnerStepper(1));
      formik.setFieldValue("currentStep", 1);
    } else if (
      formik.values.livingInCanada === "justCurious" &&
      innerStepper === 5
    ) {
      dispatch(setInnerStepper(4));
      formik.setFieldValue("currentStep", 4);
    } else if (
      formik.values.livingInCanada === "justCurious" &&
      innerStepper === 4
    ) {
      dispatch(setInnerStepper(1));
      formik.setFieldValue("currentStep", 1);
    } else if (formik.values.livingInCanada === "yes" && innerStepper === 2) {
      dispatch(setInnerStepper(1));
      formik.setFieldValue("currentStep", 1);
    } else if (innerStepper > 0) {
      dispatch(setInnerStepper(innerStepper - 1));
      formik.setFieldValue("currentStep", formik.values.currentStep - 1);
    }
    if (formik.values.answered > 0) {
      formik.setFieldValue("answered", Number(formik.values.answered || 0) - 1);
    }
  };

  const getTotalQuestions = () => {
    if (formik.values?.livingInCanada === "yes") {
      dispatch(setTotalQuestions(8));
    } else {
      dispatch(setTotalQuestions(7));
    }
  };

  const checkConfirmDisabled = () => {
    if (innerStepper === 6 || foundItem?.IS_REVIEW) {
      const foundChildren = formik.values.houseHold?.find(
        (i) => i.type === "children"
      );
      const foundMembers = formik.values.houseHold?.find(
        (i) => i.type === "otherMembers"
      );
      if (foundChildren && foundChildren?.count <= 0) {
        return true;
      }
      if (foundMembers && foundMembers?.count <= 0) {
        return true;
      }
    }
    return false;
  };
  const getTotalAnswers = () => {
    let num = 0;
    const {
      firstName,
      ageRange,
      livingInCanada,
      yearsLivingInCanada,
      province,
      provinceNew,
      city,
      cityNew,
      selectedStatus,
      houseHold,
    } = formik.values;
    if (ageRange && firstName) {
      num += 2;
    }
    if (livingInCanada) {
      num += 1;
    }
    if (livingInCanada === "yes" && yearsLivingInCanada) {
      num += 1;
    }
    if ((province && city) || (provinceNew && cityNew)) {
      num += 2;
    }
    if (selectedStatus) {
      num += 1;
    }
    if (houseHold?.length > 0) {
      num += 1;
    }
    dispatch(setAnsweredQuestions(num));
  };

  useEffect(() => {
    getTotalQuestions();
    getTotalAnswers();
  }, [formik.values]);

  const fetchUserData = () => {
    dispatch(setLoading(true));
    getUserProfile()
      .then((resp) => {
        const { data } = resp;
        const noOrCurious =
          (data?.livingInCanada === "no" ||
            data?.livingInCanada === "justCurious") &&
          data?.step === 2;
        formik.setValues({
          firstName: data.firstName || data?.fullName || "",
          ageRange: data.ageRange || "",
          livingInCanada: data.livingInCanada,
          yearsLivingInCanada: data.yearsLivingInCanada || undefined,
          province: data.province || undefined,
          city: data.city || undefined,
          provinceNew: data.province || undefined,
          cityNew: data.city || undefined,
          selectedStatus: data.immigrationStatus || "",
          houseHold:
            data?.houseHold?.length > 0
              ? data?.houseHold
              : [{ type: "myself" }],
          currentStep: noOrCurious ? 1 : data?.step || innerStepper,
        });
        if (noOrCurious) {
          setRespStep(1);
        } else {
          setRespStep(data?.step);
        }
      })
      .catch((Err) => {
        message.error(Err.message[i18n.language]);
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  useEffect(() => {
    fetchUserData();
  }, []);
  const getFilteredArr = () => {
    let filteredArr = [...stepper].filter((i) => !i.IS_FINAL && i.step != 7);
    if (formik.values.livingInCanada === "justCurious") {
      return filteredArr.filter((i) => i.step != 2 && i.step != 3);
    } else if (formik.values.livingInCanada === "no") {
      return filteredArr.filter((i) => i.step != 2 && i.step != 3);
    } else if (formik.values.livingInCanada === "yes") {
      return filteredArr.filter((i) => i.step != 4);
    }
    return filteredArr;
  };

  const checkSubmitDisabled = () => {
    const {
      currentStep,
      firstName,
      ageRange,
      livingInCanada,
      yearsLivingInCanada,
      province,
      city,
      provinceNew,
      cityNew,
      selectedStatus,
      houseHold,
    } = formik.values;
    if (foundItem?.IS_REVIEW) {
      if (livingInCanada === "yes") {
        if (!province || !city) {
          return true;
        }
      } else if (livingInCanada === "no" || livingInCanada === "justCurious") {
        if (!provinceNew || !cityNew) {
          return true;
        }
      }
    }
    if (
      (currentStep === 0 || foundItem?.IS_REVIEW) &&
      (!firstName || !ageRange)
    ) {
      return true;
    } else if (currentStep === 1 && !livingInCanada) {
      return true;
    } else if (currentStep === 2 && !yearsLivingInCanada) {
      return true;
    } else if (currentStep === 3 && (!province || !city)) {
      return true;
    } else if (currentStep === 4 && (!provinceNew || !cityNew)) {
      return true;
    } else if (currentStep === 5 && !selectedStatus) {
      return true;
    } else if (currentStep === 6 && houseHold?.length === 0) {
      return true;
    }
    return false;
  };
  useEffect(() => {
    if (containerRef) {
      containerRef.current.scrollTo({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
      window.scrollTo({ top: 0 });
    }
  }, [containerRef, innerStepper]);
  return (
    <div
      className={`income-details-right ${
        foundItem?.step === 0 || foundItem?.IS_FINAL ? "previous-hidden" : ""
      }`}
      ref={containerRef}
    >
      {/*header*/}
      <form onSubmit={formik.handleSubmit}>
        <CreateProfileHeader
          handlePreviousStep={handlePreviousStep}
          step={foundItem?.step}
          IS_FINAL={foundItem?.IS_FINAL}
        />
        {foundItem?.step != 0 && !foundItem?.IS_FINAL && (
          <div className="only-mobile-show">
            <Button
              className="previous-btn w-auto d-flex align-items-center border-0 shadow-none rounded-0 bg-transparent"
              onClick={handlePreviousStep}
            >
              <img
                src="/assets/images/icons/back-arrow-gray-icon.svg"
                className="img-fluid"
                alt=""
              />
              {t("prevQues")}
            </Button>
          </div>
        )}
        <CreateProfileCenterWrapper
          className={foundItem?.IS_FINAL ? "is-final" : ""}
        >
          {innerStepper === 7 ? (
            <div className="row">
              <div className="col-12 review-inform-div">
                <h6 className="review-tag">{t("reviewInfo")}</h6>
                {getFilteredArr().map((Item) => {
                  return (
                    <Item.Component
                      onFinish={onFinish}
                      formik={formik}
                      handleEditableStep={(num) => setEditableStep(num)}
                      editableStep={editableStep}
                      IS_EDITABLE={foundItem?.IS_EDITABLE}
                      savingsObj={savingsObj}
                      handleSavingsObj={(data) => setSavingsObj(data)}
                      IS_REVIEW={true}
                      key={Item?.step}
                    />
                  );
                })}
              </div>
            </div>
          ) : foundItem ? (
            <foundItem.Component
              onFinish={onFinish}
              formik={formik}
              handleEditableStep={(num) => setEditableStep(num)}
              editableStep={editableStep}
              IS_EDITABLE={foundItem?.IS_EDITABLE}
              savingsObj={savingsObj}
              handleSavingsObj={(data) => setSavingsObj(data)}
            />
          ) : (
            <></>
          )}
        </CreateProfileCenterWrapper>
        {!foundItem?.IS_FINAL && (
          <CreateProfileFooter
            IS_EDITABLE={foundItem?.IS_EDITABLE}
            handleEditableStep={setEditableStep}
            editableStep={editableStep}
            savingsObj={savingsObj}
            emptySavingObj={() => setSavingsObj(null)}
            formik={formik}
            IS_SKIPPABLE={foundItem?.IS_SKIPPABLE}
            handleNextStep={handleNextStep}
            innerStepper={innerStepper}
            checkSubmitDisabled={checkSubmitDisabled}
            checkConfirmDisabled={checkConfirmDisabled}
            IS_ABOUT
            IS_REVIEW={foundItem?.IS_REVIEW}
          />
        )}
      </form>
    </div>
  );
};

export default AboutYouChild;
